import { Dispatch } from 'react-redux';
import { StoreState } from '../../../reducers';
import { DownloadMode, DownloadStatus } from '../reducer';
import NativeApi, { DownloadPriority } from '../../../utils/nativeApi';
import UserAgentUtils from '../../../utils/userAgent';
import DriveItemUtils from '../../../utils/driveItem';
import UrlUtils from '../../../utils/url';
import Utils, { downloadsFolder, tempFolder } from '../../../utils';
import { Api } from '../../../typings/api';
import { createNotification } from '../../NotificationBar/actions';
import ApiUtils from '../../../utils/api';
import DownloadManagerUtils from '../../../utils/downloadManager';
import { getOrFetchDriveItemMeta } from '../../DriveItemList/actions';
import { transformTemporaryDownload, updateDependents, setIsDownloadingAllStatus,
	addItem, updateDownloadStatus, updateDownload, setRequestId,
	recordDownloadProgress
} from './store';
import StatsUtils from '../../../utils/statistics';

import { nativeDelete } from './delete';
const Q = require('q');

const requestWebDownloadItem = (driveItem: Api.DriveItem) => (dispatch: Dispatch<StoreState>, getState: () => StoreState): void => {
	const { galleryInfos } = getState();
	const { config } = galleryInfos;

	if (Utils.isMaster(config)) {
		window.open(UrlUtils.getItemOriginalFileUrl(config, driveItem.originalFile), '_blank');
	} else {
		window.location.href = UrlUtils.getAttachmentUrl(config, driveItem.id);
	}
};

const requestMobileAppDownload = (driveItem: Api.DriveItem, requestedMode: DownloadMode, dependentId?: number) =>
	(dispatch: Dispatch<StoreState>, getState: () => StoreState): Q.Promise<{} | void> => {

	const { downloadManager } = getState();
	const itemFromDownloadManager = downloadManager.items[driveItem.id] ? downloadManager.items[driveItem.id] : undefined;

	if (itemFromDownloadManager) {
		const { mode, version, status } = itemFromDownloadManager;

		if (DownloadManagerUtils.isItemDownloading(downloadManager, driveItem.id)) {
			// TODO: ???
			return Q.Promise.resolve();
		}

		if (status === DownloadStatus.ERROR) {
			return dispatch(downloadItemWithDependencies(driveItem, requestedMode, dependentId));
		}

		switch (mode) {
			case DownloadMode.Temp:
				return dispatch(transformTemporaryDownload(driveItem, itemFromDownloadManager, dependentId));
			case DownloadMode.Dependency:
				return dispatch(updateDependents(driveItem, itemFromDownloadManager, requestedMode, dependentId));
			default:
				if (DownloadManagerUtils.isLatestVersion(driveItem.version, version)) {
					return dispatch(updateDependents(driveItem, itemFromDownloadManager, requestedMode, dependentId));
				}
				return dispatch(downloadItemWithDependencies(driveItem, mode, dependentId));
		}
	} else {
		dispatch(addItem(driveItem, requestedMode, [], []));
		return dispatch(downloadItemWithDependencies(driveItem, requestedMode, dependentId));
	}
};

const getDependencies = (driveItem: Api.DriveItem) => (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<number[]> => {
	switch (driveItem.type) {
		case Api.DriveItemType.Publication:
		case Api.DriveItemType.Component:
		case Api.DriveItemType.Template:
		case Api.DriveItemType.Gallery:
			const { config } = getState().galleryInfos;
			return ApiUtils.getOutcomings(config.clientId, driveItem.id)
				.then((response: Api.Outcoming) => {
					if (response.outcomings) {
						return response.outcomings.map((outcoming) => outcoming.id);
					}
					return [];
				});
		default:
			return Q.Promise.resolve([]);
	}
};

const downloadItemWithDependencies = (driveItem: Api.DriveItem, mode: DownloadMode, dependentId?: number) =>
	(dispatch: Dispatch<StoreState>, getState: () => StoreState): Q.Promise<{} | void> => {

		const dependencyPromise: Q.Promise<number[]> = Q.Promise.resolve(dispatch(getDependencies(driveItem)));
		// Do Q.Promise.resolve([]) if driveItem isn't a component or a publication ?

		dispatch(updateDownloadStatus(driveItem.id, DownloadStatus.GETTING_DEPENDENCIES));

		return dependencyPromise
			.then(dependencies => {
				const { downloadManager } = getState();
				const itemFromDownloadManager = downloadManager.items[driveItem.id];
				const currentDependents = itemFromDownloadManager ? itemFromDownloadManager.dependents : [];
				const dependents: number[] = dependentId !== undefined ? [ dependentId, ...currentDependents ] : currentDependents;

				dispatch(updateDownload(driveItem, mode, dependencies, dependents));
				let promise: Q.Promise<{} | void> = dispatch(nativeDownload(driveItem, mode));

				dependencies.forEach(dependencyId => {
					promise = promise
						.then(() => dispatch(getOrFetchDriveItemMeta(dependencyId, false)))
						.then((dependency: Api.DriveItem) => dispatch(requestMobileAppDownload(dependency, DownloadMode.Dependency, driveItem.id)));
				});
				return promise;
			})
			.catch((e) => {
				dispatch(updateDownloadStatus(driveItem.id, DownloadStatus.ERROR));
				dispatch(createNotification('error.download_fail', { id: driveItem.id }));
				dispatch(nativeDelete(driveItem));
				return Promise.reject({});
			});
	};

const nativeDownload = (driveItem: Api.DriveItem, mode: DownloadMode) => (dispatch: Dispatch<StoreState>, getState: () => StoreState): Q.Promise<void> => {

	const { galleryInfos, driveList } = getState();
	const { config } = galleryInfos;
	const { resolutions } = driveList;
	const fileName = driveItem.type === Api.DriveItemType.Image ? DriveItemUtils.getDownloadedFileName(driveItem) : `${driveItem.id}.zip`;
	const url = driveItem.type === Api.DriveItemType.Image ? DriveItemUtils.getTheaterImage(driveItem, config, resolutions) : UrlUtils.getResourceZipUrl(config, driveItem.id);
	const destFolder = mode === DownloadMode.Temp ? tempFolder : downloadsFolder;
	const isUnzipNeeded = driveItem.type !== Api.DriveItemType.Image;
	const downloadRequest = NativeApi.downloadFile(galleryInfos.config, url, destFolder, fileName, DownloadPriority.High);

	dispatch(setRequestId(driveItem.id, downloadRequest.requestId));

	return downloadRequest.promise
		.progress(progress => {
			if (mode === DownloadMode.Requested || mode === DownloadMode.Temp) {
				dispatch(recordDownloadProgress(progress, driveItem.id));
			}
		})
		.then(() => isUnzipNeeded ? NativeApi.unzipFile(`${destFolder}/${fileName}`) : Q.Promise.resolve())
		.then(() => isUnzipNeeded ? NativeApi.removeFile(`${destFolder}/${fileName}`) : Q.Promise.resolve())
		.then(() => dispatch(updateDownloadStatus(driveItem.id, DownloadStatus.NATIVE_DOWNLOADED)));
};

const downloadAll = () => (dispatch: Dispatch<StoreState>, getState: () => StoreState): Q.Promise<{} | void> => {
	const { applicationGallery } = getState().galleryInfos.config;
	StatsUtils.trackDownloadAll();
	dispatch(setIsDownloadingAllStatus(true));

	return dispatch(downloadRecursively(applicationGallery, DownloadMode.Requested))
		.finally(() => dispatch(setIsDownloadingAllStatus(false)));
};

const downloadRecursively = (driveItemId: number, mode: DownloadMode) =>
	(dispatch: Dispatch<StoreState>, getState: () => StoreState): Q.Promise<{} | void> => {
		const { driveList, downloadManager } = getState();

		if (!UserAgentUtils.isOnline()) {
			dispatch(createNotification('info.offline_download_multiple_assets'));
			return Q.Promise.reject({});
		} else if (!DownloadManagerUtils.isItemPersistentlyDownloaded(downloadManager, driveItemId)
				|| !DownloadManagerUtils.isLatestVersion(driveList.driveItems[driveItemId].version, downloadManager.items[driveItemId].version)) {
			const { driveItems } = driveList;
			const item = driveItems[driveItemId];
			const { resources, type } = item;
			let promise: Q.Promise<{} | void> = DriveItemUtils.isDownloadableItem(type)
					? dispatch(requestMobileAppDownload(item, mode))
					: Q.Promise.resolve();

			if (resources) {
				resources.forEach(resource => {
					promise = promise.finally(() => dispatch(downloadRecursively(resource.id, mode)));
				});
			}
			return promise;
		}

		return Q.Promise.resolve();
	};

export { requestWebDownloadItem, requestMobileAppDownload, downloadRecursively, downloadAll };



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/DownloadManager/actions/download.ts